import React, { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  HistoryTab as HistoryTabUI,
  EventFilter,
  HistoryTabTranslations,
  HistoryEvent,
  AnalysisSectionHeader,
  Loading,
  media,
} from '@popety-io/ui'
import { useEstimateHistoryData } from './hooks/useEstimateHistoryData'
import { useClearMapSection } from '../hooks'
import {
  HistoryEventDetail,
  DetailWrapper,
  ContentScroller,
} from './HistoryEventDetail'
import { createStaticHistoryServices } from '../../../services/staticHistoryService'

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  min-height: 500px;

  ${media.mobileOnly} {
    min-height: 300px;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  min-height: 400px;

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.large};
    min-height: 200px;
  }
`

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 14px;

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.large};
  }
`

interface HistoryTabProps {
  estimateData: any
  hash?: string
}

export const HistoryTab: React.FC<HistoryTabProps> = ({ estimateData }) => {
  const { t } = useTranslation(['explore', 'history', 'common'])

  // Clear map section data when viewing history
  useClearMapSection()

  const { events, loading, error } = useEstimateHistoryData(estimateData)

  // Create static services for loading transaction/listing details
  const { loadTransaction, loadListing } = useMemo(() => {
    return createStaticHistoryServices(estimateData)
  }, [estimateData])

  // Track which filters are active
  const [activeFilterTypes, setActiveFilterTypes] = useState<Set<string>>(
    new Set(['permit', 'transaction', 'rental', 'sale'])
  )

  // Track selected event for detail view
  const [selectedEvent, setSelectedEvent] = useState<HistoryEvent | null>(null)

  // Handle event click
  const handleEventClick = useCallback((event: HistoryEvent) => {
    setSelectedEvent(event)
  }, [])

  // Calculate filters with counts
  const filters: EventFilter[] = useMemo(() => {
    const counts = events.reduce(
      (acc, event) => {
        acc[event.type] = (acc[event.type] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )

    return [
      {
        type: 'permit',
        label: t('propertyPanel.history.permits'),
        icon: 'building-permit',
        count: counts.permit || 0,
        active: activeFilterTypes.has('permit'),
      },
      {
        type: 'transaction',
        label: t('propertyPanel.history.transactions'),
        icon: 'real-estate-transaction',
        count: counts.transaction || 0,
        active: activeFilterTypes.has('transaction'),
      },
      {
        type: 'rental',
        label: t('propertyPanel.history.rentals'),
        icon: 'listing-rental',
        count: counts.rental || 0,
        active: activeFilterTypes.has('rental'),
      },
      {
        type: 'sale',
        label: t('propertyPanel.history.sales'),
        icon: 'listing-purchase',
        count: counts.sale || 0,
        active: activeFilterTypes.has('sale'),
      },
    ]
  }, [events, t, activeFilterTypes])

  const toggleFilter = (type: string) => {
    setActiveFilterTypes((prev) => {
      // If only this filter is active, activate all filters
      if (prev.size === 1 && prev.has(type)) {
        return new Set(['permit', 'transaction', 'rental', 'sale'])
      }
      // Otherwise, make only this filter active
      return new Set([type])
    })
  }

  const translations: HistoryTabTranslations = {
    loading: t('common.loading'),
    error: t('common.error'),
    noEvents: t('history.timeline.noEvents'),
    adjustFilters: t('common.noResults'),
    eventDetails: t('history.eventDetails'),
    back: t('history.back'),
    // Detail view translations
    propertyForRent: t('history.detailView.propertyForRent'),
    propertyForSale: t('history.detailView.propertyForSale'),
    addressNotSpecified: t('history.detailView.addressNotSpecified'),
    published: t('history.detailView.published'),
    statusLabel: t('history.detailView.statusLabel'),
    active: t('history.detailView.active'),
    inactive: t('history.detailView.inactive'),
    onlineFor: t('history.detailView.onlineFor'),
    days: t('history.detailView.days'),
    rooms: t('history.detailView.rooms'),
    livingArea: t('history.detailView.livingArea'),
    landArea: t('history.detailView.landArea'),
    bathrooms: t('history.detailView.bathrooms'),
    floor: t('history.detailView.floor'),
    built: t('history.detailView.built'),
    description: t('history.detailView.description'),
    propertyDetails: t('history.detailView.propertyDetails'),
    propertyType: t('history.detailView.propertyType'),
    propertyCategory: t('history.detailView.propertyCategory'),
    constructionYear: t('history.detailView.constructionYear'),
    renovationYear: t('history.detailView.renovationYear'),
    available: t('history.detailView.available'),
    immediately: t('history.detailView.immediately'),
    furnished: t('history.detailView.furnished'),
    petsAllowed: t('history.detailView.petsAllowed'),
    energyLabel: t('history.detailView.energyLabel'),
    heatingType: t('history.detailView.heatingType'),
    propertyAgents: t('history.detailView.propertyAgents'),
    featuresAndAmenities: t('history.detailView.featuresAndAmenities'),
    parking: t('history.detailView.parking'),
    balcony: t('history.detailView.balcony'),
    terrace: t('history.detailView.terrace'),
    garden: t('history.detailView.garden'),
    garage: t('history.detailView.garage'),
    elevator: t('history.detailView.elevator'),
    // Section translations for detail views
    sections: {
      transactionDetails: t('history.sections.transactionDetails'),
      parties: t('history.sections.parties'),
      propertyInfo: t('history.sections.propertyInfo'),
      workDescription: t('history.sections.workDescription'),
      permitDetails: t('history.sections.permitDetails'),
      projectInfo: t('history.sections.projectInfo'),
      inquiryPeriod: t('history.sections.inquiryPeriod'),
      // Transaction detail sections
      transactionInfo: t('history.transactionDetail.sections.transactionInfo'),
      participants: t('history.transactionDetail.sections.participants'),
      properties: t('history.transactionDetail.sections.properties'),
      summary: t('history.transactionDetail.sections.summary'),
    },
    // Field translations for detail views
    fields: {
      date: t('history.fields.date'),
      reason: t('history.fields.reason'),
      ownershipType: t('history.fields.ownershipType'),
      buildingClassification: t('history.fields.buildingClassification'),
      buyers: t('history.fields.buyers'),
      sellers: t('history.fields.sellers'),
      area: t('history.fields.area'),
      units: t('history.fields.units'),
      previousTransaction: t('history.fields.previousTransaction'),
      permitNumber: t('history.fields.permitNumber'),
      workType: t('history.fields.workType'),
      classification: t('history.fields.classification'),
      projectArea: t('history.fields.projectArea'),
      owner: t('history.fields.owner'),
      architect: t('history.fields.architect'),
      projectValue: t('history.fields.projectValue'),
      inquiryStart: t('history.fields.inquiryStart'),
      inquiryEnd: t('history.fields.inquiryEnd'),
      // Transaction detail fields
      totalPrice: t('history.transactionDetail.fields.totalPrice'),
      transactionDate: t('history.transactionDetail.fields.transactionDate'),
      transactionId: t('history.transactionDetail.fields.transactionId'),
      officialId: t('history.transactionDetail.fields.officialId'),
      transactionType: t('history.transactionDetail.fields.transactionType'),
      publicationDate: t('history.transactionDetail.fields.publicationDate'),
      description: t('history.fields.description'),
      participant: t('history.transactionDetail.fields.participant'),
      role: t('history.transactionDetail.fields.role'),
      participantType: t('history.transactionDetail.fields.participantType'),
      share: t('history.transactionDetail.fields.share'),
      property: t('history.transactionDetail.fields.property'),
      city: t('history.transactionDetail.fields.city'),
      postalCode: t('history.transactionDetail.fields.postalCode'),
      landArea: t('history.fields.landArea'),
      totalSurface: t('history.transactionDetail.fields.totalSurface'),
      legalType: t('history.transactionDetail.fields.legalType'),
      propertyNumber: t('history.transactionDetail.fields.propertyNumber'),
      numberOfBuyers: t('history.transactionDetail.fields.numberOfBuyers'),
      numberOfSellers: t('history.transactionDetail.fields.numberOfSellers'),
      numberOfProperties: t(
        'history.transactionDetail.fields.numberOfProperties'
      ),
    },
    permit: {
      title: t('history.timeline.buildingPermit'),
      fields: {
        permitNumber: t('history.fields.permitNumber'),
        workType: t('history.fields.workType'),
        owner: t('history.fields.owner'),
        architect: t('history.fields.architect'),
        projectValue: t('history.fields.projectValue'),
        projectArea: t('history.fields.projectArea'),
        classification: t('history.fields.classification'),
      },
    },
    transaction: {
      title: t('history.timeline.transaction'),
      fields: {
        buyers: t('history.fields.buyers'),
        sellers: t('history.fields.sellers'),
        reason: t('history.fields.reason'),
        ownershipType: t('history.fields.ownershipType'),
        buildingClassification: t('history.fields.buildingClassification'),
        area: t('history.fields.area'),
        units: t('history.fields.units'),
        previousTransaction: t('history.fields.previousTransaction'),
      },
    },
    rental: {
      title: t('history.timeline.rentalListing'),
      fields: {
        propertyType: t('history.fields.propertyType'),
        propertyCategory: t('history.fields.propertyCategory'),
        area: t('history.fields.area'),
        rooms: t('history.fields.rooms'),
        bathrooms: t('history.fields.bathrooms'),
        floor: t('history.fields.floor'),
        constructionYear: t('history.fields.constructionYear'),
        broker: t('history.fields.broker'),
        availableDate: t('history.fields.availableDate'),
        furnished: t('history.fields.furnished'),
        petsAllowed: t('history.fields.petsAllowed'),
        parking: t('history.fields.parking'),
        balcony: t('history.fields.balcony'),
        terrace: t('history.fields.terrace'),
        garden: t('history.fields.garden'),
        energyLabel: t('history.fields.energyLabel'),
      },
      status: {
        active: t('history.status.active'),
        inactive: t('history.status.inactive'),
      },
      footer: {
        online: t('history.timeline.online'),
        days: t('history.timeline.days'),
      },
    },
    sale: {
      title: t('history.timeline.saleListing'),
      fields: {
        propertyType: t('history.fields.propertyType'),
        propertyCategory: t('history.fields.propertyCategory'),
        area: t('history.fields.area'),
        landArea: t('history.fields.landArea'),
        rooms: t('history.fields.rooms'),
        bathrooms: t('history.fields.bathrooms'),
        floor: t('history.fields.floor'),
        constructionYear: t('history.fields.constructionYear'),
        renovationYear: t('history.fields.renovationYear'),
        broker: t('history.fields.broker'),
        garage: t('history.fields.garage'),
        parking: t('history.fields.parking'),
        balcony: t('history.fields.balcony'),
        terrace: t('history.fields.terrace'),
        garden: t('history.fields.garden'),
        elevator: t('history.fields.elevator'),
        energyLabel: t('history.fields.energyLabel'),
        heatingType: t('history.fields.heatingType'),
      },
      status: {
        active: t('history.status.active'),
        inactive: t('history.status.inactive'),
      },
      footer: {
        online: t('history.timeline.online'),
        days: t('history.timeline.days'),
      },
    },
  }

  // Show loading or error states
  if (loading) {
    return (
      <Container>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          {t('common.error')}: {error}
        </ErrorMessage>
      </Container>
    )
  }

  if (!estimateData) {
    return (
      <Container>
        <ErrorMessage>{t('history.noEstimateDataAvailable')}</ErrorMessage>
      </Container>
    )
  }

  return (
    <DetailWrapper>
      <ContentScroller>
        <Container>
          <AnalysisSectionHeader
            title={t('history.propertyHistory')}
            subtitle={t('history.propertyHistorySubtitle')}
          />
          <HistoryTabUI
            events={events}
            loading={loading}
            error={error}
            filters={filters}
            onFilterToggle={toggleFilter}
            onEventClick={handleEventClick}
            selectedEvent={selectedEvent}
            onCloseDetail={() => setSelectedEvent(null)}
            disableInlineDetail={true}
            translations={translations}
            loadTransaction={loadTransaction}
          />
        </Container>
      </ContentScroller>
      <HistoryEventDetail
        isOpen={!!selectedEvent}
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        translations={translations}
        loadTransaction={loadTransaction}
        loadListing={loadListing}
      />
    </DetailWrapper>
  )
}
